import React, { useState } from "react";
import { Alert, Box, Button, Checkbox, DialogActions, DialogContent, DialogTitle, Divider, IconButton, Modal, ModalDialog, ModalOverflow, Stack, Tooltip, Typography } from "@mui/joy";
import AddAlarmRoundedIcon from '@mui/icons-material/AddAlarmRounded';
import { formatDate, isValidDate } from "@utils/dates";
import ObjectPreview from "./objects/ObjectPreview";
import { FormProvider, useForm } from "react-hook-form";
import { FieldEditTable } from "./FieldEditTable";
import { useUpdateTask } from "@api/taskService";
import toast from "react-hot-toast";
import { queryClient } from "@api/queryClient";
import { authenticatedApi } from "api";
import { useAuth } from "@providers/AuthProvider";
import { useLocation } from "react-router-dom";

const fields = [
    {
        name: 'time_taken',
        label: 'Time Taken (in hours)',
        type: 'number',
        placeholder: 'e.g. 0.5 or 6',
        config: {
            // required: true,
            validate: (value, object) => {
                if (!value && !object.mark_completed) return 'Required if not marking as complete';
                return true;
            },
        },
    },
    {
        name: 'volunteered_at',
        label: 'Date',
        type: 'date',
        placeholder: 'e.g. 05/23/2016',
        config: {
            required: true,
        },
    },
    {
        name: 'notes',
        label: 'Notes',
        type: 'text',
        placeholder: 'e.g. Board Meeting',
    },
];

export function AddTimeModal({
    open,
    setOpen,
    contact = null,
    task = null,
    onLogTime = null,
}) {
    const { user } = useAuth();
    const { hash, pathname, search } = useLocation();

    let defaultValues = {
        volunteered_at: new Date(),
    };

    if(task) {
        defaultValues.task_id = task.id;
    }

    const formMethods = useForm({
        defaultValues,
    });
    const {
          setValue,
          reset,
          handleSubmit,
          watch,
          getValues,
          formState: { isSubmitting, errors, isValid, isDirty }
    } = formMethods;

    const updateTask = useUpdateTask({id: task?.id ?? 0});

    // check for dashboard vs contact vs work plan
    const performQueryKeyInvalidations = () => {
        if(pathname.includes('/work-plans')) {
            queryClient.invalidateQueries({ queryKey: ['work-plans'] });
        }

        if(pathname.includes('/contacts')) {
            queryClient.invalidateQueries({ queryKey: ['contacts'] });
        }

        if(pathname.includes('/dashboards')) {
            queryClient.invalidateQueries({ queryKey: ['tasks'] });
        }
    };

    const onMarkIncomplete = () => {
        const performUpdate = updateTask.mutateAsync({
            completed_at: null
        });
        toast.promise(performUpdate, {
            loading: `Updating...`,
            success: `The task was marked as incomplete.`,
            error: (err) => `There was an error while saving.`,
        });
        performUpdate.then(() => {
            performQueryKeyInvalidations();
            // TODO: figure out how to invalidate the current screen
            // queryClient.invalidateQueries({ queryKey: ['work-plans'] });
            // queryClient.invalidateQueries({ queryKey: [`searchselect_${endpoint}`] });
          reset();
          setOpen(false);
        });
    }

    const onSubmit = (fields) => {
        if(fields.mark_completed) {
            const performUpdate = updateTask.mutateAsync({
                completed_at: new Date(),
            });

            if(!fields.time_taken) {
                toast.promise(performUpdate, {
                    loading: `Adding...`,
                    success: `The time was added${fields.mark_completed ? ' and the task was marked as complete' : ''}!`,
                    error: (err) => `There was an error while saving.`,
                });
            }

            performUpdate.then(() => {
                performQueryKeyInvalidations();

                if(!fields.time_taken) {
                    setOpen(false);
                }
            });
        }

        if(fields.time_taken) {
            let hoursToAdd = [
                {
                    contact_id: contact?.id ?? user.contact.id,
                    is_staff: contact?.is_staff ?? user.contact.is_staff,
                    task_id: task?.id ?? null,
                    time_taken: fields.time_taken,
                    volunteered_at: fields.volunteered_at,
                    // involvement_id: fields.involvement_id,
                    notes: fields.notes,
                }
            ];
            const performTimeUpdate = authenticatedApi.post(`/volunteer-hours`, hoursToAdd);
            toast.promise(performTimeUpdate, {
                loading: `Adding...`,
                success: `The time was added${fields.mark_completed ? ' and the task was marked as complete' : ''}!`,
                error: (err) => `There was an error while saving.`,
            });
            performTimeUpdate.then((response) => {
                reset();
                setOpen(false);

                try {
                    onLogTime(response.data);
                } catch(e) {
                    //
                }
            });
        }
    };

    return (
    <Modal open={open} onClose={() => setOpen(false)}>
        <ModalOverflow>
        <form onSubmit={handleSubmit(onSubmit)}>
        <FormProvider {...formMethods}>
            <ModalDialog sx={{width: '100%', maxWidth: '800px'}}>
                <DialogTitle>
                    {task ? <>Add Time to <em>{task.task_name}</em></> : 'Add Time'}
                </DialogTitle>
                <Divider/>

                {task && task.completed_at && (
                    <Alert
                        variant="soft"
                        color="neutral"
                        endDecorator={
                            <Button onClick={onMarkIncomplete}>Mark Incomplete</Button>
                        }
                    >
                        <Typography><b>Note:</b> This task has been completed.</Typography>
                    </Alert>
                )}

                <Box sx={{
                    marginInline: 'var(--Divider-inset)'
                }}
                >
                    <FieldEditTable mode="column" fields={fields}/>
                </Box>

                {task && !task.completed_at && (
                    <Checkbox
                        name="mark_completed"
                        {...formMethods.register('mark_completed')}
                        label={<Stack>
                            <Typography>Mark task as complete</Typography>
                            <Typography fontSize={'sm'}>You will still have the ability to add time to completed tasks.</Typography>
                        </Stack>}
                    />
                )}

                <DialogActions>
                    <Button
                        disabled={!isValid}
                        type="button" // this can't be submit type or it will submit the parent form
                        onClick={() => {
                        onSubmit(getValues());
                        }}
                    >
                        Save
                    </Button>
                    <Button
                        variant="plain"
                        color="neutral"
                        onClick={() => {
                            setOpen(false);
                            try {
                                // onCancel();
                            } catch(e) {
                                //
                            }
                        }}
                    >
                        Cancel
                    </Button>

                    {contact === null && (
                        <Alert
                            color="warning"
                            variant="soft"
                        >
                            <Typography><b>Note:</b> This will add volunteer hours for <b>your</b> account.</Typography>
                        </Alert>
                    )}
                </DialogActions>
            </ModalDialog>
      </FormProvider>
      </form>
        </ModalOverflow>
    </Modal>
    )
}
